import React, { useEffect, useState } from "react";
import {
  Button,
  Input,
  useToast,
} from "@swordsweeper/ssi-ui-component-library";
import { useFormHandlers } from "@swordsweeper/react-forms";
import * as Yup from "yup";
import Modal from "@src/components/common/Modal";
import { ModalSizes } from "@src/components/common/Modal/ModalSizes";
import useYupFieldErrors from "@src/hooks/useYupFieldErrors";
import * as ClinicApi from "@src/api/ClinicApi";
import { Clinic } from "@src/types/clinic";
import { useCurrentUserStore } from "@src/state/currentUserState";
import styles from "./EditClinicInfoModal.module.scss";

const phoneRegex =
  /^\(?([0-9]{3})\)?\s*[-.]?\s*([0-9]{3})\s*[-.]?\s*([0-9]{4})$/;

const schema = Yup.object().shape({
  clinicName: Yup.string().trim().required("Clinic name is required."),
  address1: Yup.string().trim().required("Street address is required."),
  address2: Yup.string().trim().nullable(),
  city: Yup.string().trim().required("City is required."),
  state: Yup.string().trim().required("State is required."),
  postalCode: Yup.string().trim().required("Postal code is required."),
  phone: Yup.string()
    .trim()
    .required("Clinic phone number is required.")
    .test(
      "phone-format",
      "Phone number must be 10 digits.",
      (v) => !v || phoneRegex.test(v),
    ),
});

interface FormData {
  clinicName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  phone: string;
}

const formatPhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

function toInitialFormData(clinic: Clinic): FormData {
  return {
    clinicName: clinic.name ?? "",
    address1: clinic.address1 ?? "",
    address2: clinic.address2 ?? "",
    city: clinic.city ?? "",
    state: clinic.state ?? "",
    postalCode: clinic.zipCode ?? "",
    phone: clinic.phone ?? "",
  };
}

interface Props {
  isShowing: boolean;
  onClose: () => void;
  clinic: Clinic;
}

export default function EditClinicInfoModal({
  isShowing,
  onClose,
  clinic,
}: Props): React.JSX.Element {
  const { showToast } = useToast();
  const { fetchUser } = useCurrentUserStore();
  const initialData = toInitialFormData(clinic);
  const { formData, handleUpdate, isValid, setFormData } =
    useFormHandlers<FormData>(initialData, schema);
  const { errors, validateField, clearErrors, applyServerFieldErrors } =
    useYupFieldErrors<FormData>(schema);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset to the latest clinic values whenever the modal reopens.
  useEffect(() => {
    if (isShowing) {
      setFormData(toInitialFormData(clinic));
      clearErrors();
    }
  }, [isShowing, clinic.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await ClinicApi.updateClinic(clinic.id, formData);
      await fetchUser();
      showToast({
        message: "Clinic information updated.",
        variant: "success",
      });
      onClose();
    } catch (error: any) {
      const body = error?.response?.body;
      if (applyServerFieldErrors(body)) return;
      showToast({
        message:
          body?.detail ||
          body?.error ||
          error?.message ||
          "Could not update clinic information. Please try again.",
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isShowing={isShowing}
      onClose={onClose}
      size={ModalSizes.Medium}
      header={<div className={styles.headerTitle}>Edit Clinic Information</div>}
    >
      <div className={styles.fieldStack}>
        <Field error={errors.clinicName}>
          <Input
            label="Clinic Name"
            defaultValue=""
            value={formData.clinicName}
            onChange={(value: string) =>
              handleUpdate({ name: "clinicName", value })
            }
            onBlur={() => validateField("clinicName", formData)}
            error={!!errors.clinicName}
            width="100%"
            required
            data-testid="edit-clinic-info-name"
          />
        </Field>

        <Field error={errors.address1}>
          <Input
            label="Clinic Address 01"
            defaultValue=""
            value={formData.address1}
            onChange={(value: string) =>
              handleUpdate({ name: "address1", value })
            }
            onBlur={() => validateField("address1", formData)}
            error={!!errors.address1}
            width="100%"
            required
            data-testid="edit-clinic-info-address-1"
          />
        </Field>

        <Field error={errors.address2}>
          <Input
            label="Clinic Address 02"
            defaultValue=""
            value={formData.address2}
            onChange={(value: string) =>
              handleUpdate({ name: "address2", value })
            }
            onBlur={() => validateField("address2", formData)}
            error={!!errors.address2}
            width="100%"
            data-testid="edit-clinic-info-address-2"
          />
        </Field>

        <Field error={errors.city}>
          <Input
            label="City"
            defaultValue=""
            value={formData.city}
            onChange={(value: string) => handleUpdate({ name: "city", value })}
            onBlur={() => validateField("city", formData)}
            error={!!errors.city}
            width="100%"
            required
            data-testid="edit-clinic-info-city"
          />
        </Field>

        <div className={styles.row}>
          <Field error={errors.state}>
            <Input
              label="State"
              defaultValue=""
              value={formData.state}
              onChange={(value: string) =>
                handleUpdate({ name: "state", value })
              }
              onBlur={() => validateField("state", formData)}
              error={!!errors.state}
              width="100%"
              required
              data-testid="edit-clinic-info-state"
            />
          </Field>

          <Field error={errors.postalCode}>
            <Input
              label="Postal Code"
              defaultValue=""
              value={formData.postalCode}
              onChange={(value: string) =>
                handleUpdate({ name: "postalCode", value })
              }
              onBlur={() => validateField("postalCode", formData)}
              error={!!errors.postalCode}
              width="100%"
              required
              data-testid="edit-clinic-info-postal-code"
            />
          </Field>
        </div>

        <Field error={errors.phone}>
          <Input
            label="Clinic Phone Number"
            defaultValue=""
            type="tel"
            inputMode="numeric"
            value={formatPhone(formData.phone)}
            onChange={(value: string) =>
              handleUpdate({
                name: "phone",
                value: value.replace(/\D/g, "").slice(0, 10),
              })
            }
            onBlur={() => validateField("phone", formData)}
            error={!!errors.phone}
            width="100%"
            required
            data-testid="edit-clinic-info-phone"
          />
        </Field>
      </div>

      <Button
        className={`btn btn-primary btn-md ${styles.submitButton}`}
        onClick={handleSubmit}
        disabled={!isValid || isSubmitting}
        data-testid="edit-clinic-info-submit"
      >
        {isSubmitting ? "Saving..." : "Save Changes"}
      </Button>
    </Modal>
  );
}

interface FieldProps {
  error?: string;
  children: React.ReactNode;
}

function Field({ error, children }: FieldProps): React.JSX.Element {
  return (
    <div className={styles.field}>
      {children}
      {error && (
        <p className={styles.errorMessage}>
          <i className="fa-light fa-circle-exclamation" aria-hidden />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}
